import { useQueryClient } from "@tanstack/react-query";
import {
  AccountMenu,
  AccountMenuDivider,
  AccountMenuHeader,
  AccountMenuItem,
  AccountMenuRow,
  AccountMenuSection,
  Avatar,
} from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { RoleLabel } from "../../features/contacts/RoleLabel.js";
import { switchBand } from "../../app/switchBand.js";
import { useActiveBand } from "../../app/activeBand.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { PendingInvitesMenuItem } from "../../features/invites/PendingInvitesMenuItem.js";
import { StarBandButton } from "../../features/shared/StarBandButton.js";

type SeatSummary = components["schemas"]["SeatSummary"];

/** Two-letter avatar mark from a name — a band ("Midnight Freight" → "MF") or, while acting as
 * someone else, a person ("Jane Doe" → "JD"). Same algorithm, so it is the same function. */
function bandInitials(name: string | null | undefined): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
}

/**
 * A seat's mark: the band's MAIN photo (Brand slot 1) when it has one, initials otherwise. A rounded square,
 * because the shape is what says "band" rather than "person".
 *
 * Each row resolves against its OWN band, not the active one — the dropdown lists other bands. A band
 * picture is public identity, served by the tenant-less global avatar route (`useAvatarUrl("band", …)`) — the
 * one band-photo path across the app, which needs no reach into the band. That hook is why the chip takes a
 * NODE rather than a src: one seat per component, so the loop can resolve each band's media on its own.
 */
function SeatMark({ seat }: { seat: SeatSummary | undefined }) {
  const url = useAvatarUrl("band", seat?.bandId, seat?.avatarMediaId);
  return <Avatar initials={bandInitials(seat?.displayName)} src={url} size="xs" shape="rounded" />;
}

type PersonSummary = components["schemas"]["PersonSummary"];

/**
 * The caller's OWN face, for the "View my profile" header (ART-238). It follows the "the artist IS the band"
 * rule the mobile account sheet already applies: an OWNER seat has no personal picture — its profile IS the
 * band profile — so an owner resolves the BAND photo (rounded square), and everyone else their person face
 * (circle). Resolved here from the active seat's `isOwner` rather than through `PersonAvatar`, because the
 * switcher renders ABOVE the shell's `BandProvider` and that component's owner→band branch needs a mounted
 * band context.
 */
function SelfMark({ person, active }: { person: PersonSummary | undefined; active: SeatSummary | undefined }) {
  const isBandIdentity = active?.isOwner === true;
  const url = useAvatarUrl(
    isBandIdentity ? "band" : "person",
    isBandIdentity ? active?.bandId : person?.personId,
    isBandIdentity ? active?.avatarMediaId : person?.avatarMediaId,
  );
  const name = isBandIdentity ? active?.displayName : person?.displayName || person?.firstName;
  return (
    <Avatar initials={bandInitials(name)} src={url} size="md" shape={isBandIdentity ? "rounded" : "circle"} />
  );
}

export interface BandSwitcherProps {
  seats: readonly SeatSummary[];
  activeBandId: string | undefined;
  /** The signed-in address, shown under "View my profile" — which account you are acting as. */
  personEmail?: string | null;
  /** The caller's own person record, for the "View my profile" avatar (ART-238). */
  person?: PersonSummary;
  onLogout: () => void;
  /** Open the caller's own profile IN PLACE (PO, 2026-08-20) — never a route change. The shell mounts
   * the modal under its BandProvider, which this switcher renders above. (The switcher's only routed
   * door went with this — onNavigate left with it.) */
  onViewProfile: () => void;
}

/**
 * The account chip + band switcher, on the shared `AccountMenu` primitive — the same panel, dismiss
 * contract and red last word the roster and org chips paint. Selecting a band runs the switchBand SCORCH —
 * the single audited path that clears the cache/stores + leaves groups, so no other band's data survives.
 */
export function BandSwitcher({ seats, activeBandId, personEmail, person, onLogout, onViewProfile }: BandSwitcherProps) {
  const queryClient = useQueryClient();
  const setActiveBand = useActiveBand((s) => s.setActiveBand);

  const bandSeats = seats.filter((s) => s.kind === "band");
  const active = bandSeats.find((s) => s.bandId === activeBandId) ?? bandSeats[0];

  async function select(bandId: string | null | undefined) {
    if (!bandId || bandId === activeBandId) return;
    await switchBand(bandId, {
      queryClient,
      // The hub connection is PERSON-bound, so a band switch must NOT tear it down — only the
      // band-scoped group membership changes, and that is declarative: ArtistShell's useLiveGroup(`band:{id}`)
      // leaves the old band group and joins the new one when activeBandId changes. So the scorch passes no
      // client here (a passed client would stop the whole person connection); the cache clear does the rest.
      realtime: null,
      // Scorch step 5: re-register push under the NEW band so payloads route to the active silo.
      // A no-op on web; on native it re-runs the registration flow bound to the new band.
      navigate: (id) => setActiveBand(id),
    });
  }

  return (
    <AccountMenu
      // Always the BAND, even while acting as someone else. The chip's job is naming which band you are
      // in, and a swap does not change that; WHO you are signed in as is the "View as" button's, which
      // glows while swapped and is always on screen rather than behind this dropdown.
      avatar={<SeatMark seat={active} />}
      name={active?.displayName ?? "Select band"}
      menuLabel="Your account and bands"
    >
      {/* The switcher OWNS the person-record door + Log out (CD TopNav: "View my profile" and
          "Log out" live in the band-switcher dropdown; Log out is deliberately NOT in Settings). */}
      <AccountMenuHeader
        label="View my profile"
        meta={personEmail}
        avatar={<SelfMark person={person} active={active} />}
        onSelect={onViewProfile}
      />
      <AccountMenuDivider />
      <AccountMenuSection>Your bands</AccountMenuSection>
      {bandSeats.map((seat) => (
        <AccountMenuRow
          key={seat.bandId}
          avatar={<SeatMark seat={seat} />}
          name={seat.displayName ?? "Band"}
          // The seat role via RoleLabel: a full-access member gets the AV mark, the owner reads "Artist", and
          // the WORDS come from the title (never the role slug — the ~70-titles rule the old helper broke).
          meta={<RoleLabel role={seat.role} titleKey={seat.titleKey} owner={seat.isOwner} />}
          mark={seat.bandId === activeBandId ? "CURRENT" : undefined}
          checked={seat.bandId === activeBandId}
          onSelect={() => void select(seat.bandId)}
          // ART-319: the band the app opens on. Beside the row, so starring never switches you to it. Only
          // with more than one band — with one, the app always opens on it and a star chooses nothing.
          trailing={seat.bandId && bandSeats.length > 1 ? <StarBandButton bandId={seat.bandId} bandName={seat.displayName ?? "this band"} /> : undefined}
        />
      ))}
      {/* No "+ New band"/"+ Create organization" here (CD parity: the dropdown is profile ·
          bands · Log out). Those doors stay reachable via the mobile AccountSheet, the org/roster
          shells, and the router's seatless→create-org redirect (the P-16 split). */}
      <AccountMenuDivider />
      {/* ART-239: present only when something is waiting — opens the root-mounted Pending invites modal. */}
      <PendingInvitesMenuItem />
      <AccountMenuItem tone="danger" onSelect={onLogout}>
        Log out
      </AccountMenuItem>
    </AccountMenu>
  );
}
